import {Pipe, PipeTransform} from '@angular/core';
interface PipeState {
    collection: any[];
    size: number;
    start: number;
    end: number;
    slice: any[];
}

@Pipe({
    name: 'paginate',
    pure: false
})
export class SynchroPaginatePipe implements PipeTransform{

    // store the values from the last time the pipe was invoked
    private state:PipeState;

    public transform(collection: any[], args: any): any {

        // When an observable is passed through the AsyncPipe, it will output
        // `null` until the subscription resolves. In this case, we want to
        // use the cached data from the `state` object to prevent the NgFor
        // from flashing empty until the real values arrive.
        if (args instanceof Array) {
          // compatible with angular2 before beta16
          args = args[0];
        }
        if (!(collection instanceof Array)) {
            if (this.state != null) {
                return this.state.slice;
            } else {
                return collection;
            }
        }

        const instance = this.createInstance(collection, args);
        let start, end;
        const perPage = instance.itemsPerPage;

        if (collection instanceof Array) {
            start = (instance.currentPage - 1) * perPage;
            end = start + perPage;

            const isIdentical = this.stateIsIdentical(collection, start, end);
            if (isIdentical) {
                return this.state.slice;
            } else {
                const slice = collection.slice(start, end);
                this.saveState(collection, slice, start, end);
                return slice;
            }
        }

        // save the state for server-side collection to avoid null
        // flash as new data loads.
        this.saveState(collection, collection, start, end);
        return collection;
    }

    /**
     * Create an PaginationInstance object, using defaults for any optional properties not supplied.
     */
    private createInstance(collection: any[], args: any): PaginationInstance {
        const config = args;
        this.checkConfig(config);

        return {
            itemsPerPage: config.itemsPerPage || 0,
            currentPage: config.currentPage || 1,
            totalItems: config.totalItems || collection.length
        };
    }

    /**
     * Ensure the argument passed to the filter contains the required properties.
     */
    private checkConfig(config: any): void {
        const required = ['itemsPerPage', 'currentPage'];

        const missing = required.filter(prop => !config.hasOwnProperty(prop));
        if (0 < missing.length) {
            throw new Error(`PaginatePipe: Argument is missing the following required properties: ${missing.join(', ')}`);
        }
    }

    /**
     * To avoid returning a brand new array each time the pipe is run, we store the state of the sliced
     * array for a given id. This means that the next time the pipe is run on this collection & id, we just
     * need to check that the collection, start and end points are all identical, and if so, return the
     * last sliced array.
     */
    private saveState(collection: any[], slice: any[], start: number, end: number) {
        this.state = {
            collection,
            size: collection.length,
            slice,
            start,
            end
        };
    }

    /**
     * For a given id, returns true if the collection, size, start and end values are identical.
     */
    private stateIsIdentical(collection: any[], start: number, end: number): boolean {
        const state = this.state;
        if (!state) {
            return false;
        }
        const isMetaDataIdentical = state.size === collection.length &&
                                  state.start === start &&
                                  state.end === end;

        if(!isMetaDataIdentical) {
            return false;
        }

        return state.slice.every((element, index) => element === collection[start + index]);
    }
}

export interface PaginationInstance {
    /**
     * The number of items per paginated page.
     */
    itemsPerPage: number;
    /**
     * The current (active) page.
     */
    currentPage: number;
    /**
     * The total number of items in the collection. Only useful when
     * doing server-side paging, where the collection size is limited
     * to a single page returned by the server API.
     *
     * For in-memory paging, this property should not be set, as it
     * will be automatically set to the value of  collection.length.
     */
    totalItems?: number;
}
